import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Plus, RefreshCw, CheckCircle2, Presentation, Sparkles, Play, ImageIcon, Download, Wand2, X, GripVertical, History, LayoutGrid, Maximize2, Pencil, Trash2, Copy } from 'lucide-react';
import toast from '../../components/Toast';
import { MaskDialog } from '../../components/MaskDialog';
import { AppShell } from '../../platform/AppShell';
import { api, type ModelConfig } from '../../services/api';
import { pptAPI, type PptAiOutlineSlide, type PptProject, type PptSlide } from './api';
import { formatBeijingDateTime } from '../../utils/time';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

type CreateForm = {
  title: string;
  baseModelId: string;
  inpaintModelId: string;
  generationQuality: 'low' | 'medium' | 'high';
};

function createDefaultForm(
  baseModelId = '',
  inpaintModelId = '',
  generationQuality: 'low' | 'medium' | 'high' = 'low'
): CreateForm {
  return {
    title: '',
    baseModelId,
    inpaintModelId,
    generationQuality,
  };
}

const DEFAULT_FORM: CreateForm = createDefaultForm();

const STYLE_OPTIONS = [
  { value: 'business', label: '商务' },
  { value: 'tech', label: '科技' },
  { value: 'minimal', label: '极简' },
  { value: 'education', label: '教育' },
  { value: 'marketing', label: '营销' },
];

const EXPORT_OPTIONS = [
  { value: 'pptx', label: '导出 PPTX', description: '导出为可交付的 PowerPoint 文件' },
  { value: 'pdf', label: '导出 PDF', description: '导出为固定版式的演示文档' },
  { value: 'zip', label: '导出 ZIP', description: '打包导出全部页面图片资源' },
] as const;

const QUALITY_OPTIONS: Array<{ value: 'low' | 'medium' | 'high'; label: string; description: string }> = [
  { value: 'low', label: '标准', description: '速度更快，适合初稿和批量生成' },
  { value: 'medium', label: '高清', description: '质量与速度平衡，适合多数 PPT 画面' },
  { value: 'high', label: '超清', description: '细节更丰富，耗时和消耗通常更高' },
];

const SLIDE_TYPE_OPTIONS = [
  { value: 'cover', label: '封面', description: '适合作为整套 PPT 的开场页' },
  { value: 'toc', label: '目录', description: '适合概览章节结构与信息分布' },
  { value: 'section', label: '章节', description: '适合作为章节过渡和分段页' },
  { value: 'content', label: '内容', description: '适合承载主要信息与核心画面' },
  { value: 'summary', label: '总结', description: '适合作为收尾、结论或行动建议' },
] as const;

function formatQualityLabel(quality: 'low' | 'medium' | 'high'): string {
  return QUALITY_OPTIONS.find((option) => option.value === quality)?.label || quality;
}

function formatSlideTypeLabel(slideType: string): string {
  return SLIDE_TYPE_OPTIONS.find((option) => option.value === slideType)?.label || slideType;
}

function validateSlideDraft(draft: SlideDraft, options?: { pageLabel?: string }): string | null {
  const pagePrefix = options?.pageLabel ? `${options.pageLabel}` : '当前页面';
  const hasValidSlideType = SLIDE_TYPE_OPTIONS.some((option) => option.value === draft.slideType);
  if (!hasValidSlideType) return `${pagePrefix}缺少页面类型`;
  if (!draft.title.trim()) return `${pagePrefix}缺少页面标题`;
  if (!draft.contentPrompt.trim()) return `${pagePrefix}缺少内容提示词`;
  return null;
}

type SlideDraft = {
  id: string;
  sortOrder: number;
  slideType: string;
  title: string;
  contentPrompt: string;
};

type AiOutlineSession = {
  sourceText: string;
  previewText: string;
  drafts: SlideDraft[];
};

const AI_OUTLINE_STORAGE_KEY = 'ppt-ai-outline-sessions';

function readAiOutlineSessionsFromStorage(): Record<string, AiOutlineSession> {
  if (typeof window === 'undefined') return {};

  try {
    const raw = window.localStorage.getItem(AI_OUTLINE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, AiOutlineSession>;
  } catch {
    return {};
  }
}

function persistAiOutlineSessionsToStorage(sessions: Record<string, AiOutlineSession>) {
  if (typeof window === 'undefined') return;

  try {
    if (Object.keys(sessions).length === 0) {
      window.localStorage.removeItem(AI_OUTLINE_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(AI_OUTLINE_STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // Ignore storage write errors to avoid blocking the editor flow.
  }
}

function toSlideDraft(slide: PptSlide): SlideDraft {
  return {
    id: slide.id,
    sortOrder: slide.sortOrder,
    slideType: slide.slideType,
    title: slide.title || '',
    contentPrompt: slide.contentPrompt || '',
  };
}

function applyAiOutlineToDrafts(baseDrafts: SlideDraft[], aiSlides: PptAiOutlineSlide[]): SlideDraft[] {
  return baseDrafts.map((draft, index) => ({
    ...draft,
    sortOrder: index,
    slideType: aiSlides[index]?.slideType || draft.slideType,
    title: aiSlides[index]?.title || draft.title,
    contentPrompt: aiSlides[index]?.contentPrompt || draft.contentPrompt,
  }));
}

function shouldPollProject(project: PptProject | null): boolean {
  if (!project) return false;
  if (project.status === 'background_pending' || project.status === 'generating') return true;
  if (project.backgroundCandidates.some((candidate) => candidate.status !== 'completed' && candidate.status !== 'failed')) return true;
  if (project.slides.some((slide) => slide.generationStatus === 'queued' || slide.generationStatus === 'processing')) return true;
  return false;
}

function slideStatusLabel(status: string): string {
  if (status === 'processing') return '生成中';
  if (status === 'queued') return '排队中';
  if (status === 'completed') return '已完成';
  if (status === 'failed') return '失败';
  return '待生成';
}

function slideStatusClass(status: string): string {
  if (status === 'processing' || status === 'queued') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'completed') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'failed') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function reorderSlides<T extends { id: string }>(items: T[], fromId: string, toId: string): T[] {
  if (fromId === toId) return items;
  const fromIndex = items.findIndex((item) => item.id === fromId);
  const toIndex = items.findIndex((item) => item.id === toId);
  if (fromIndex < 0 || toIndex < 0) return items;
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

function formatModelLabel(model: ModelConfig): string {
  return model.description?.trim() ? `${model.name} · ${model.description}` : model.name;
}

function getModelGenerationCost(
  model: ModelConfig | null | undefined,
  quality: 'low' | 'medium' | 'high',
  mode: 'text2img' | 'img2img' | 'inpaint'
): number | null {
  if (!model) return null;
  const qualityCost = model.credits?.[quality];
  if (typeof qualityCost !== 'number') return null;
  const modeCost = model.modeCredits?.[mode] ?? 0;
  return qualityCost + modeCost;
}

function resolvePreviewModel(
  models: ModelConfig[],
  preferredModelId: string | null | undefined
): ModelConfig | null {
  if (preferredModelId) {
    const matched = models.find((model) => model.id === preferredModelId);
    if (matched) return matched;
  }
  return models[0] || null;
}

function formatCreditButtonLabel(label: string, cost: number | null, multiplier = 1): string {
  if (typeof cost !== 'number' || !Number.isFinite(cost)) return label;
  return `${label} ${cost * multiplier} 积分`;
}

function buildSlidePromptPreview(input: {
  projectTitle: string;
  slideType: string;
  title?: string | null;
  contentPrompt?: string | null;
}): string {
  return [
    `为 PPT 的 ${input.slideType} 页面生成整页图片。`,
    `项目主题：${input.projectTitle}。`,
    input.title ? `页面标题：${input.title}。` : '',
    input.contentPrompt ? `内容提示词：${input.contentPrompt}。` : '',
    '要求：必须以统一背景图为唯一参考图，保持整套 PPT 视觉一致；横版幻灯片构图清晰，适合图片式 PPT，尽量避免生成过多细碎小字。',
  ]
    .filter(Boolean)
    .join('\n');
}

export default function PptPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const aiOutlinePreviewRef = useRef<HTMLTextAreaElement | null>(null);
  const [siteName, setSiteName] = useState('AI 图片生成');
  const [projects, setProjects] = useState<PptProject[]>([]);
  const [currentProject, setCurrentProject] = useState<PptProject | null>(null);
  const [availableModels, setAvailableModels] = useState<ModelConfig[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [form, setForm] = useState<CreateForm>(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [backgroundBusy, setBackgroundBusy] = useState(false);
  const [slideGenerating, setSlideGenerating] = useState(false);
  const [selectingJobId, setSelectingJobId] = useState<string | null>(null);
  const [slideDrafts, setSlideDrafts] = useState<SlideDraft[]>([]);
  const [actingSlideId, setActingSlideId] = useState<string | null>(null);
  const [maskSlideId, setMaskSlideId] = useState<string | null>(null);
  const [addingSlide, setAddingSlide] = useState(false);
  const [deleteSlideId, setDeleteSlideId] = useState<string | null>(null);
  const [deletingSlideId, setDeletingSlideId] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<'pptx' | 'pdf' | 'zip' | null>(null);
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<CreateForm>(DEFAULT_FORM);
  const [savingProject, setSavingProject] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [backgroundDialogOpen, setBackgroundDialogOpen] = useState(false);
  const [backgroundPromptDraft, setBackgroundPromptDraft] = useState('');
  const [aiOutlineDialogOpen, setAiOutlineDialogOpen] = useState(false);
  const [aiOutlineSourceText, setAiOutlineSourceText] = useState('');
  const [aiOutlinePreviewText, setAiOutlinePreviewText] = useState('');
  const [aiOutlineDrafts, setAiOutlineDrafts] = useState<SlideDraft[]>([]);
  const [aiOutlineSessions, setAiOutlineSessions] = useState<Record<string, AiOutlineSession>>(() => readAiOutlineSessionsFromStorage());
  const [aiOutlineStreaming, setAiOutlineStreaming] = useState(false);
  const [aiOutlineError, setAiOutlineError] = useState<string | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [batchGenerateDialogOpen, setBatchGenerateDialogOpen] = useState(false);
  const [previewLightboxOpen, setPreviewLightboxOpen] = useState(false);
  const [draggingSlideId, setDraggingSlideId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [restoringVariantId, setRestoringVariantId] = useState<string | null>(null);

  const resolvedCurrentProject = useMemo(() => {
    if (projectId) {
      return currentProject?.id === projectId
        ? currentProject
        : projects.find((project) => project.id === projectId) || null;
    }
    return currentProject || projects[0] || null;
  }, [currentProject, projectId, projects]);

  const baseModelOptions = useMemo(
    () => availableModels.filter((model) => model.enableText2img && model.enableImg2img),
    [availableModels]
  );
  const inpaintModelOptions = useMemo(
    () => availableModels.filter((model) => model.enableInpaint),
    [availableModels]
  );
  const creditsPreview = useMemo(() => {
    if (!resolvedCurrentProject) {
      return {
        background: null,
        batch: null,
        single: null,
        inpaint: null,
      };
    }

    const quality = resolvedCurrentProject.generationQuality || 'low';
    const baseModel = resolvePreviewModel(baseModelOptions, resolvedCurrentProject.baseModelId);
    const inpaintModel = resolvePreviewModel(inpaintModelOptions, resolvedCurrentProject.inpaintModelId);
    const singleSlideCost = getModelGenerationCost(baseModel, quality, 'img2img');

    return {
      background: getModelGenerationCost(baseModel, quality, 'text2img'),
      batch: typeof singleSlideCost === 'number' ? singleSlideCost * slideDrafts.length : null,
      single: singleSlideCost,
      inpaint: getModelGenerationCost(inpaintModel, quality, 'inpaint'),
    };
  }, [baseModelOptions, inpaintModelOptions, resolvedCurrentProject, slideDrafts.length]);

  useEffect(() => {
    api.getSiteName().then((res) => setSiteName(res.siteName)).catch(() => undefined);
  }, []);

  useEffect(() => {
    void loadProjects();
    void loadModels();
  }, []);

  useEffect(() => {
    setForm((prev) => {
      const nextBaseModelId = baseModelOptions.some((model) => model.id === prev.baseModelId)
        ? prev.baseModelId
        : (baseModelOptions[0]?.id || '');
      const nextInpaintModelId = inpaintModelOptions.some((model) => model.id === prev.inpaintModelId)
        ? prev.inpaintModelId
        : (inpaintModelOptions[0]?.id || '');

      if (prev.baseModelId === nextBaseModelId && prev.inpaintModelId === nextInpaintModelId) {
        return prev;
      }

      return {
        ...prev,
        baseModelId: nextBaseModelId,
        inpaintModelId: nextInpaintModelId,
      };
    });
  }, [baseModelOptions, inpaintModelOptions]);

  useEffect(() => {
    if (!editProjectId) return;
    setEditForm((prev) => ({
      ...prev,
      baseModelId: baseModelOptions.some((model) => model.id === prev.baseModelId) ? prev.baseModelId : (baseModelOptions[0]?.id || ''),
      inpaintModelId: inpaintModelOptions.some((model) => model.id === prev.inpaintModelId) ? prev.inpaintModelId : (inpaintModelOptions[0]?.id || ''),
    }));
  }, [editProjectId, baseModelOptions, inpaintModelOptions]);

  useEffect(() => {
    if (!projectId) return;
    void loadProject(projectId);
  }, [projectId]);

  useEffect(() => {
    if (!resolvedCurrentProject) {
      setSlideDrafts([]);
      return;
    }
    setSlideDrafts(resolvedCurrentProject.slides.map(toSlideDraft));
  }, [resolvedCurrentProject?.id, resolvedCurrentProject?.updatedAt]);

  useEffect(() => {
    if (!resolvedCurrentProject?.slides.length) {
      setSelectedSlideId(null);
      return;
    }
    const exists = resolvedCurrentProject.slides.some((slide) => slide.id === selectedSlideId);
    if (!selectedSlideId || !exists) {
      setSelectedSlideId(resolvedCurrentProject.slides[0].id);
    }
  }, [resolvedCurrentProject?.id, resolvedCurrentProject?.updatedAt, selectedSlideId]);

  useEffect(() => {
    if (!projectId || !shouldPollProject(resolvedCurrentProject)) return;
    const timer = window.setInterval(() => {
      void loadProject(projectId);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [projectId, resolvedCurrentProject]);

  useEffect(() => {
    const previewElement = aiOutlinePreviewRef.current;
    if (!previewElement) return;
    previewElement.scrollTop = previewElement.scrollHeight;
  }, [aiOutlinePreviewText]);

  useEffect(() => {
    persistAiOutlineSessionsToStorage(aiOutlineSessions);
  }, [aiOutlineSessions]);

  async function loadProjects() {
    setLoading(true);
    try {
      const res = await pptAPI.listProjects();
      setProjects(res.data);
      if (!projectId && res.data.length > 0) {
        setCurrentProject(res.data[0]);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载 PPT 项目失败');
    } finally {
      setLoading(false);
    }
  }

  async function loadModels() {
    setModelsLoading(true);
    try {
      const res = await api.getModels();
      setAvailableModels(res.data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载模型列表失败');
    } finally {
      setModelsLoading(false);
    }
  }

  async function loadProject(id: string) {
    setLoading(true);
    try {
      const res = await pptAPI.getProject(id);
      setCurrentProject(res.data);
      setProjects((prev) => {
        const next = prev.filter((item) => item.id !== res.data.id);
        return [res.data, ...next];
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载 PPT 项目详情失败');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateProject() {
    if (!form.title.trim()) {
      toast.error('请先填写项目标题');
      return;
    }
    if (!form.baseModelId) {
      toast.error('请选择基础模型');
      return;
    }
    if (!form.inpaintModelId) {
      toast.error('请选择重绘模型');
      return;
    }

    setCreating(true);
    try {
      const res = await pptAPI.createProject({
        title: form.title.trim(),
        baseModelId: form.baseModelId,
        inpaintModelId: form.inpaintModelId,
        generationQuality: form.generationQuality,
        pageCount: 8,
        aspectRatio: '16:9',
        stylePreset: 'business',
      });
      setForm(createDefaultForm(baseModelOptions[0]?.id || '', inpaintModelOptions[0]?.id || ''));
      setCreateDialogOpen(false);
      setSidebarOpen(false);
      setCurrentProject(res.data);
      setProjects((prev) => [res.data, ...prev.filter((item) => item.id !== res.data.id)]);
      setSelectedSlideId(res.data.slides[0]?.id || null);
      navigate(`/apps/ppt/${res.data.id}`);
      toast.success('PPT 项目已创建');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '创建 PPT 项目失败');
    } finally {
      setCreating(false);
    }
  }

  function openCreateDialog() {
    if (!availableModels.length && !modelsLoading) {
      void loadModels();
    }
    setCreateDialogOpen(true);
  }

  function closeCreateDialog() {
    if (creating) return;
    setCreateDialogOpen(false);
    setForm(createDefaultForm(baseModelOptions[0]?.id || '', inpaintModelOptions[0]?.id || ''));
  }

  function openEditProjectDialog(project: PptProject) {
    setEditProjectId(project.id);
    setEditForm({
      title: project.title || '',
      baseModelId: baseModelOptions.some((model) => model.id === project.baseModelId) ? (project.baseModelId || '') : (baseModelOptions[0]?.id || ''),
      inpaintModelId: inpaintModelOptions.some((model) => model.id === project.inpaintModelId) ? (project.inpaintModelId || '') : (inpaintModelOptions[0]?.id || ''),
      generationQuality: project.generationQuality || 'low',
    });
  }

  function closeEditProjectDialog() {
    if (savingProject) return;
    setEditProjectId(null);
    setEditForm(createDefaultForm(baseModelOptions[0]?.id || '', inpaintModelOptions[0]?.id || ''));
  }

  function openDeleteProjectDialog(project: PptProject) {
    setDeleteProjectId(project.id);
  }

  function closeDeleteProjectDialog() {
    if (deletingProjectId !== null) return;
    setDeleteProjectId(null);
  }

  function openDeleteSlideDialog(slideId: string) {
    setDeleteSlideId(slideId);
  }

  function closeDeleteSlideDialog() {
    if (deletingSlideId !== null) return;
    setDeleteSlideId(null);
  }

  async function handleUpdateProject() {
    if (!editProjectId) return;
    if (!editForm.title.trim()) {
      toast.error('请先填写项目标题');
      return;
    }
    if (!editForm.baseModelId) {
      toast.error('请选择基础模型');
      return;
    }
    if (!editForm.inpaintModelId) {
      toast.error('请选择重绘模型');
      return;
    }

    setSavingProject(true);
    try {
      const res = await pptAPI.updateProject(editProjectId, {
        title: editForm.title.trim(),
        baseModelId: editForm.baseModelId,
        inpaintModelId: editForm.inpaintModelId,
        generationQuality: editForm.generationQuality,
      });
      applyProjectUpdate(res.data);
      setEditProjectId(null);
      setEditForm(createDefaultForm(baseModelOptions[0]?.id || '', inpaintModelOptions[0]?.id || ''));
      toast.success('项目配置已更新');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '更新项目失败');
    } finally {
      setSavingProject(false);
    }
  }

  async function handleDeleteProject() {
    if (!deleteProjectId) return;

    setDeletingProjectId(deleteProjectId);
    try {
      await pptAPI.deleteProject(deleteProjectId);
      const remainingProjects = projects.filter((project) => project.id !== deleteProjectId);
      const deletedCurrent = resolvedCurrentProject?.id === deleteProjectId;

      setProjects(remainingProjects);
      setDeleteProjectId(null);
      setSidebarOpen(false);

      if (deletedCurrent) {
        const nextProject = remainingProjects[0] || null;
        setCurrentProject(nextProject);
        setSelectedSlideId(nextProject?.slides[0]?.id || null);
        navigate(nextProject ? `/apps/ppt/${nextProject.id}` : '/apps/ppt');
      } else if (currentProject?.id === deleteProjectId) {
        setCurrentProject(null);
      }

      toast.success('项目已删除');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除项目失败');
    } finally {
      setDeletingProjectId(null);
    }
  }

  async function handleGenerateBackground() {
    const project = resolvedCurrentProject;
    if (!project) {
      toast.error('请先创建 PPT 项目');
      return;
    }

    setBackgroundBusy(true);
    try {
      const res = await pptAPI.generateBackgroundCandidates(project.id, {
        candidateCount: 1,
        prompt: backgroundPromptDraft.trim() || undefined,
      });
      setCurrentProject(res.data);
      setProjects((prev) => [res.data, ...prev.filter((item) => item.id !== res.data.id)]);
      toast.success('背景候选已开始生成');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '生成背景候选失败');
    } finally {
      setBackgroundBusy(false);
    }
  }

  function openBackgroundDialog() {
    setBackgroundPromptDraft(resolvedCurrentProject?.backgroundPrompt || '');
    setBackgroundDialogOpen(true);
  }

  function closeBackgroundDialog() {
    if (backgroundBusy) return;
    setBackgroundDialogOpen(false);
  }

  function openAiOutlineDialog() {
    if (!resolvedCurrentProject) {
      toast.error('请先创建 PPT 项目');
      return;
    }
    const session = aiOutlineSessions[resolvedCurrentProject.id];
    setAiOutlineSourceText(session?.sourceText || '');
    setAiOutlinePreviewText(session?.previewText || '');
    setAiOutlineDrafts(session?.drafts || []);
    setAiOutlineError(null);
    setAiOutlineDialogOpen(true);
  }

  function closeAiOutlineDialog() {
    if (aiOutlineStreaming) return;
    setAiOutlineDialogOpen(false);
    setAiOutlineError(null);
  }

  function updateAiOutlineSession(projectId: string, patch: Partial<AiOutlineSession>) {
    setAiOutlineSessions((prev) => {
      const current = prev[projectId] || {
        sourceText: '',
        previewText: '',
        drafts: [],
      };
      return {
        ...prev,
        [projectId]: {
          ...current,
          ...patch,
        },
      };
    });
  }

  function handleAiOutlineSourceTextChange(value: string) {
    const projectId = resolvedCurrentProject?.id;
    setAiOutlineSourceText(value);
    if (!projectId) return;
    updateAiOutlineSession(projectId, { sourceText: value });
  }

  function handleClearAiOutline() {
    const projectId = resolvedCurrentProject?.id;
    if (!projectId || aiOutlineStreaming) return;
    setAiOutlineSourceText('');
    setAiOutlinePreviewText('');
    setAiOutlineDrafts([]);
    setAiOutlineError(null);
    setAiOutlineSessions((prev) => {
      if (!prev[projectId]) return prev;
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }

  function openExportDialog() {
    if (!hasGeneratedSlides || exportingFormat !== null) return;
    setExportDialogOpen(true);
  }

  function openBatchGenerateDialog() {
    const project = resolvedCurrentProject;
    if (!project) {
      toast.error('请先创建 PPT 项目');
      return;
    }
    if (!project.backgroundImageJobId) {
      toast.error('请先选定全局背景');
      return;
    }
    for (const slide of slideDrafts) {
      const errorMessage = validateSlideDraft(slide, { pageLabel: `第 ${slide.sortOrder + 1} 页` });
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
    }
    if (slideGenerating) return;
    setBatchGenerateDialogOpen(true);
  }

  function closeBatchGenerateDialog() {
    if (slideGenerating) return;
    setBatchGenerateDialogOpen(false);
  }

  function closeExportDialog() {
    if (exportingFormat !== null) return;
    setExportDialogOpen(false);
  }

  function openInpaintDialog(slideId: string) {
    setMaskSlideId(slideId);
  }

  async function handleSelectBackground(jobId: string) {
    const project = resolvedCurrentProject;
    if (!project) return;

    setSelectingJobId(jobId);
    try {
      const res = await pptAPI.selectBackground(project.id, jobId);
      setCurrentProject(res.data);
      setProjects((prev) => [res.data, ...prev.filter((item) => item.id !== res.data.id)]);
      toast.success('背景已设为当前 PPT 的全局参考图');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '选择背景失败');
    } finally {
      setSelectingJobId(null);
    }
  }

  function handleSlideDraftChange(slideId: string, patch: Partial<SlideDraft>) {
    setSlideDrafts((prev) => prev.map((slide) => (slide.id === slideId ? { ...slide, ...patch } : slide)));
  }

  function handleAiOutlineDraftChange(slideId: string, patch: Partial<SlideDraft>) {
    const projectId = resolvedCurrentProject?.id;
    setAiOutlineDrafts((prev) => {
      const next = prev.map((slide) => (slide.id === slideId ? { ...slide, ...patch } : slide));
      if (projectId) {
        updateAiOutlineSession(projectId, { drafts: next });
      }
      return next;
    });
  }

  function getSlideDraft(slide: PptSlide): SlideDraft {
    return slideDrafts.find((item) => item.id === slide.id) || toSlideDraft(slide);
  }

  function applyProjectUpdate(project: PptProject) {
    setCurrentProject(project);
    setProjects((prev) => [project, ...prev.filter((item) => item.id !== project.id)]);
  }

  const editingProject = editProjectId ? projects.find((project) => project.id === editProjectId) || null : null;
  const deletingProject = deleteProjectId ? projects.find((project) => project.id === deleteProjectId) || null : null;
  const deletingSlide = deleteSlideId && resolvedCurrentProject
    ? resolvedCurrentProject.slides.find((slide) => slide.id === deleteSlideId) || null
    : null;

  const selectedSlide = useMemo(() => {
    if (!resolvedCurrentProject?.slides.length) return null;
    return resolvedCurrentProject.slides.find((slide) => slide.id === selectedSlideId) || resolvedCurrentProject.slides[0];
  }, [resolvedCurrentProject, selectedSlideId]);

  const selectedSlideDraft = selectedSlide ? getSlideDraft(selectedSlide) : null;
  const selectedSlideType = selectedSlideDraft?.slideType || selectedSlide?.slideType || 'content';
  const selectedSlidePromptPreview = selectedSlideDraft && resolvedCurrentProject
    ? buildSlidePromptPreview({
        projectTitle: resolvedCurrentProject.title,
        slideType: selectedSlideDraft.slideType,
        title: selectedSlideDraft.title.trim(),
        contentPrompt: selectedSlideDraft.contentPrompt.trim(),
      })
    : '';
  const generatedSlideCount = resolvedCurrentProject?.slides.filter((slide) => Boolean(slide.image?.resultUrl)).length || 0;
  const stylePresetLabel =
    STYLE_OPTIONS.find((option) => option.value === resolvedCurrentProject?.stylePreset)?.label || resolvedCurrentProject?.stylePreset || '-';
  const hasGeneratedSlides = resolvedCurrentProject?.slides.some((slide) => Boolean(slide.image?.resultUrl)) || false;
  const canApplyAiOutline = aiOutlineDrafts.length > 0
    && aiOutlineDrafts.every((draft, index) => !validateSlideDraft(draft, { pageLabel: `第 ${index + 1} 页` }));

  async function handleStartAiOutlineGeneration() {
    const project = resolvedCurrentProject;
    if (!project) {
      toast.error('请先创建 PPT 项目');
      return;
    }
    if (!aiOutlineSourceText.trim()) {
      toast.error('请先输入用于生成大纲的资料');
      return;
    }

    const baseDrafts = project.slides.map((slide) => getSlideDraft(slide));
    let receivedResult = false;
    let streamError: string | null = null;

    setAiOutlineStreaming(true);
    setAiOutlineError(null);
    setAiOutlinePreviewText('');
    setAiOutlineDrafts([]);
    updateAiOutlineSession(project.id, {
      sourceText: aiOutlineSourceText,
      previewText: '',
      drafts: [],
    });

    try {
      await pptAPI.streamAiOutline(project.id, aiOutlineSourceText.trim(), {
        onChunk: (chunk) => {
          setAiOutlinePreviewText((prev) => {
            const next = prev + chunk;
            updateAiOutlineSession(project.id, { previewText: next });
            return next;
          });
        },
        onResult: (result) => {
          receivedResult = true;
          const nextDrafts = applyAiOutlineToDrafts(baseDrafts, result.slides);
          setAiOutlineDrafts(nextDrafts);
          updateAiOutlineSession(project.id, { drafts: nextDrafts });
        },
        onError: (error) => {
          streamError = error;
          setAiOutlineError(error);
        },
      });

      if (streamError) {
        toast.error(streamError);
        return;
      }
      if (!receivedResult) {
        const message = 'AI 未返回可用大纲，请重试';
        setAiOutlineError(message);
        toast.error(message);
        return;
      }
      toast.success('AI 大纲已生成，请先预览并确认');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'AI 生成大纲失败';
      setAiOutlineError(message);
      toast.error(message);
    } finally {
      setAiOutlineStreaming(false);
    }
  }

  function handleApplyAiOutline() {
    if (!canApplyAiOutline) {
      toast.error('请先完成大纲生成并确认每页内容已填写');
      return;
    }
    setSlideDrafts(aiOutlineDrafts.map((draft, index) => ({
      ...draft,
      sortOrder: index,
    })));
    setAiOutlineDialogOpen(false);
    setAiOutlineError(null);
    toast.success('AI 大纲已填充到幻灯片草稿');
  }

  async function handleConfirmGenerateSlides() {
    const project = resolvedCurrentProject;
    if (!project) {
      toast.error('请先创建 PPT 项目');
      return;
    }
    if (!project.backgroundImageJobId) {
      toast.error('请先选定全局背景');
      return;
    }
    for (const slide of slideDrafts) {
      const errorMessage = validateSlideDraft(slide, { pageLabel: `第 ${slide.sortOrder + 1} 页` });
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
    }

    setBatchGenerateDialogOpen(false);
    setSlideGenerating(true);
    try {
      const res = await pptAPI.generateSlides(project.id, {
        slides: slideDrafts.map((slide) => ({
          slideId: slide.id,
          sortOrder: slide.sortOrder,
          slideType: slide.slideType,
          title: slide.title.trim(),
          contentPrompt: slide.contentPrompt.trim(),
        })),
      });
      applyProjectUpdate(res.data);
      toast.success('页面批量生成已提交');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '批量生成页面失败');
    } finally {
      setSlideGenerating(false);
    }
  }

  async function handleRegenerateSlide(slide: PptSlide) {
    const draft = getSlideDraft(slide);
    const errorMessage = validateSlideDraft(draft);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setActingSlideId(slide.id);
    try {
      const res = await pptAPI.regenerateSlide(slide.id, {
        slideType: draft.slideType,
        title: draft.title.trim(),
        contentPrompt: draft.contentPrompt.trim(),
      });
      applyProjectUpdate(res.data);
      toast.success('单页重生成已提交');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '单页重生成失败');
    } finally {
      setActingSlideId(null);
    }
  }

  async function handleSubmitInpaint({ maskDataUrl, prompt }: { maskDataUrl: string; prompt: string }) {
    const project = resolvedCurrentProject;
    const slide = project?.slides.find((item) => item.id === maskSlideId);
    if (!slide) {
      setMaskSlideId(null);
      return;
    }

    const draft = getSlideDraft(slide);
    setActingSlideId(slide.id);
    try {
      const res = await pptAPI.inpaintSlide(slide.id, {
        maskImage: maskDataUrl,
        editPrompt: prompt.trim(),
        slideType: draft.slideType,
        title: draft.title.trim(),
        contentPrompt: draft.contentPrompt.trim(),
      });
      applyProjectUpdate(res.data);
      setMaskSlideId(null);
      toast.success('局部重绘已提交');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '局部重绘失败');
    } finally {
      setActingSlideId(null);
    }
  }

  async function handleExport(format: 'pptx' | 'pdf' | 'zip') {
    const project = resolvedCurrentProject;
    if (!project) {
      toast.error('请先创建 PPT 项目');
      return;
    }

    setExportDialogOpen(false);
    setExportingFormat(format);
    try {
      const blob = await pptAPI.exportProjectBundle(project.id, format);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.title || 'presentation'}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`已导出 ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '导出失败');
    } finally {
      setExportingFormat(null);
    }
  }

  async function handleSlideReorder(fromId: string, toId: string) {
    const project = resolvedCurrentProject;
    if (!project || fromId === toId) return;
    const reorderedProjectSlides = reorderSlides(project.slides, fromId, toId).map((slide, index) => ({
      ...slide,
      sortOrder: index,
    }));
    const reorderedDrafts = reorderSlides(slideDrafts, fromId, toId).map((slide, index) => ({
      ...slide,
      sortOrder: index,
    }));

    const previousProject = resolvedCurrentProject;
    const previousProjects = projects;
    setReordering(true);
    applyProjectUpdate({
      ...project,
      slides: reorderedProjectSlides,
    });
    setSlideDrafts(reorderedDrafts);

    try {
      const res = await pptAPI.reorderSlides(project.id, reorderedProjectSlides.map((slide) => slide.id));
      applyProjectUpdate(res.data);
      setSlideDrafts(res.data.slides.map(toSlideDraft));
      toast.success('页面顺序已更新');
    } catch (error) {
      setCurrentProject(previousProject);
      setProjects(previousProjects);
      if (previousProject) {
        setSlideDrafts(previousProject.slides.map(toSlideDraft));
      }
      toast.error(error instanceof Error ? error.message : '页面排序失败');
    } finally {
      setReordering(false);
      setDraggingSlideId(null);
    }
  }

  async function handleCreateSlide() {
    const project = resolvedCurrentProject;
    if (!project) return;

    setAddingSlide(true);
    try {
      const res = await pptAPI.createSlide(project.id, {
        afterSlideId: selectedSlide?.id || undefined,
      });
      applyProjectUpdate(res.data);
      setSlideDrafts(res.data.slides.map(toSlideDraft));
      setSelectedSlideId(res.meta?.slideId || res.data.slides[res.data.slides.length - 1]?.id || null);
      toast.success('已新增幻灯片');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '新增幻灯片失败');
    } finally {
      setAddingSlide(false);
    }
  }

  async function handleDeleteSlide() {
    const project = resolvedCurrentProject;
    if (!project || !deleteSlideId) return;

    const deletingIndex = project.slides.findIndex((slide) => slide.id === deleteSlideId);
    if (deletingIndex < 0) {
      setDeleteSlideId(null);
      return;
    }
    if (project.slides.length <= 1) {
      toast.error('至少保留 1 页，无法删除当前页面');
      return;
    }

    const nextSelectedSlideId = selectedSlideId === deleteSlideId
      ? (project.slides[deletingIndex + 1]?.id || project.slides[deletingIndex - 1]?.id || null)
      : selectedSlideId;

    setDeletingSlideId(deleteSlideId);
    try {
      const res = await pptAPI.deleteSlide(deleteSlideId);
      applyProjectUpdate(res.data);
      setSlideDrafts(res.data.slides.map(toSlideDraft));
      setSelectedSlideId(nextSelectedSlideId && res.data.slides.some((slide) => slide.id === nextSelectedSlideId)
        ? nextSelectedSlideId
        : (res.data.slides[0]?.id || null));
      setDeleteSlideId(null);
      toast.success('幻灯片已删除');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除幻灯片失败');
    } finally {
      setDeletingSlideId(null);
    }
  }

  async function handleRestoreVariant(slideId: string, jobId: string) {
    setRestoringVariantId(jobId);
    try {
      const res = await pptAPI.restoreSlideVariant(slideId, jobId);
      applyProjectUpdate(res.data);
      toast.success('已恢复到该历史版本');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '恢复历史版本失败');
    } finally {
      setRestoringVariantId(null);
    }
  }

  async function handleCopySlidePrompt() {
    if (!selectedSlidePromptPreview) {
      toast.error('当前没有可复制的提示词');
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedSlidePromptPreview);
      toast.success('最终提示词已复制');
    } catch {
      toast.error('复制失败，请检查浏览器权限');
    }
  }

  return (
    <AppShell
      appName="图片式 PPT 工作台"
      appSubtitle={`${siteName} · 背景先行、整套批量生成、图片式导出`}
      appIcon={<Presentation className="h-5 w-5" />}
      backgroundClassName="bg-[#eef3f8]"
      onOpenSidebar={() => setSidebarOpen(true)}
      headerRight={
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {resolvedCurrentProject && (
            <>
              <div className="hidden rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600 md:block">
                {generatedSlideCount}/{resolvedCurrentProject.slides.length} 已出图
              </div>
              <button
                onClick={openAiOutlineDialog}
                disabled={aiOutlineStreaming}
                className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-lg shadow-slate-200/70 transition hover:border-violet-200 hover:text-violet-700 disabled:cursor-not-allowed disabled:opacity-50 lg:flex"
              >
                {aiOutlineStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                AI生成
              </button>
              <button
                onClick={openBackgroundDialog}
                disabled={backgroundBusy}
                className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-lg shadow-slate-200/70 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50 lg:flex"
              >
                {backgroundBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                生成背景
              </button>
              <button
                onClick={openBatchGenerateDialog}
                disabled={!resolvedCurrentProject.backgroundImageJobId || slideGenerating}
                className="hidden items-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 lg:flex"
              >
                {slideGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                一键生成
              </button>
              <button
                onClick={openExportDialog}
                disabled={!hasGeneratedSlides || exportingFormat !== null}
                className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-lg shadow-slate-200/70 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50 lg:flex"
              >
                {exportingFormat !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                导出
              </button>
            </>
          )}
          <button
            onClick={() => void loadProjects()}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-2xl bg-slate-950 px-3 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">刷新工作台</span>
            <span className="sm:hidden">刷新</span>
          </button>
        </div>
      }
    >
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-28 left-[18%] h-80 w-80 rounded-full bg-sky-200/50 blur-3xl" />
        <div className="absolute right-[-8%] top-40 h-96 w-96 rounded-full bg-cyan-200/40 blur-3xl" />
        <div className="absolute bottom-[-12%] left-[42%] h-96 w-96 rounded-full bg-amber-100/60 blur-3xl" />
      </div>

      <div className="relative lg:flex">
        <aside className="hidden h-full overflow-y-auto border-white/70 bg-white/75 shadow-[12px_0_40px_rgba(15,23,42,0.04)] backdrop-blur-xl lg:sticky lg:top-16 lg:block lg:h-[calc(100vh-64px)] lg:w-80 lg:shrink-0 lg:border-r">
          <div className="p-5">
            <div className="mb-5 rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-2xl shadow-slate-300/40">
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/10">
                  <LayoutGrid className="h-4 w-4 text-sky-200" />
                </span>
                <h2 className="text-lg font-semibold leading-none">PPT 项目库</h2>
              </div>
              <div className="mt-5">
                <button
                  onClick={openCreateDialog}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:scale-[1.01] hover:bg-sky-50"
                >
                  <Plus className="h-4 w-4" />
                  新建 PPT
                </button>
              </div>
            </div>

            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">项目列表</div>
            <div className="space-y-2">
              {projects.length === 0 && (
                <div className="rounded-[28px] border border-dashed border-slate-200 bg-white/70 p-8 text-center">
                  <Presentation className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                  <p className="text-sm font-medium text-slate-700">暂无 PPT 项目</p>
                  <p className="mt-1 text-xs text-slate-400">创建一个项目开始生成</p>
                </div>
              )}
              {projects.map((project) => {
                const active = resolvedCurrentProject?.id === project.id;
                const projectStyleLabel = STYLE_OPTIONS.find((option) => option.value === project.stylePreset)?.label || project.stylePreset;
                return (
                  <div
                    key={project.id}
                    className={`w-full rounded-3xl border p-3 text-left transition ${
                      active
                        ? 'border-sky-200 bg-sky-50/90 shadow-lg shadow-sky-100/70'
                        : 'border-white/80 bg-white/80 hover:border-slate-200 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          navigate(`/apps/ppt/${project.id}`);
                          setSidebarOpen(false);
                        }}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="truncate text-sm font-semibold text-slate-900">{project.title}</div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          {project.pageCount} 页 · {projectStyleLabel} · {project.status}
                        </div>
                      </button>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEditProjectDialog(project)}
                          disabled={savingProject || deletingProjectId === project.id}
                          className="rounded-xl p-2 text-slate-500 transition hover:bg-white hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                          title="编辑项目"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteProjectDialog(project)}
                          disabled={deletingProjectId === project.id}
                          className="rounded-xl p-2 text-slate-500 transition hover:bg-white hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                          title="删除项目"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden">
            <button
              type="button"
              aria-label="关闭 PPT 侧栏"
              className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-[86vw] max-w-[360px] overflow-hidden border-r border-white/70 bg-white shadow-2xl shadow-slate-950/25">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div className="text-sm font-semibold text-slate-900">PPT 项目库</div>
                <button type="button" onClick={() => setSidebarOpen(false)} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="h-full overflow-y-auto p-4">
                <div className="mb-4 rounded-[24px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-4 text-white">
                  <div className="mb-3 text-sm font-semibold">快速创建项目</div>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setSidebarOpen(false);
                        openCreateDialog();
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950"
                    >
                      <Plus className="h-4 w-4" />
                      新建 PPT
                    </button>
                    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs leading-5 text-slate-300">
                      点击按钮后会弹出输入标题的小窗口。
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className={`w-full rounded-3xl border px-3 py-3 text-left transition ${
                        resolvedCurrentProject?.id === project.id
                          ? 'border-sky-200 bg-sky-50/90 shadow-md shadow-sky-100/70'
                          : 'border-white/80 bg-white/80'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            navigate(`/apps/ppt/${project.id}`);
                            setSidebarOpen(false);
                          }}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="truncate text-sm font-medium text-slate-900">{project.title}</div>
                          <div className="mt-1 text-xs text-slate-500">{project.pageCount} 页 · {project.status}</div>
                        </button>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              setSidebarOpen(false);
                              openEditProjectDialog(project);
                            }}
                            disabled={savingProject || deletingProjectId === project.id}
                            className="rounded-xl p-2 text-slate-500 transition hover:bg-white hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                            title="编辑项目"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openDeleteProjectDialog(project)}
                            disabled={deletingProjectId === project.id}
                            className="rounded-xl p-2 text-slate-500 transition hover:bg-white hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                            title="删除项目"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <main className="min-h-[calc(100vh-64px)] flex-1 overflow-y-auto lg:h-[calc(100vh-64px)]">
          {loading && !resolvedCurrentProject ? (
            <div className="flex h-full items-center justify-center">
              <div className="rounded-3xl bg-white/80 p-6 shadow-xl shadow-slate-200/80">
                <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
              </div>
            </div>
          ) : resolvedCurrentProject ? (
            <div className="mx-auto max-w-[1480px] px-3 py-4 sm:px-6 sm:py-8">
              <section className="overflow-hidden rounded-[28px] border border-white/70 bg-white/85 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:rounded-[36px]">
                <div className="grid gap-0 xl:grid-cols-[220px_minmax(0,4fr)_280px] 2xl:grid-cols-[240px_minmax(0,4fr)_320px]">
                  <aside className="border-b border-slate-100 bg-slate-50/80 xl:border-b-0 xl:border-r">
                    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4">
                      <div className="text-sm font-semibold text-slate-900">幻灯片</div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-slate-400">{resolvedCurrentProject.slides.length} 页</div>
                        <button
                          type="button"
                          onClick={() => void handleCreateSlide()}
                          disabled={addingSlide}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {addingSlide ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                          新增
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 p-3 sm:p-4 xl:max-h-[calc(100vh-180px)] xl:overflow-y-auto">
                      {resolvedCurrentProject.slides.map((slide) => {
                        const slideDraft = getSlideDraft(slide);
                        const canDeleteSlide = resolvedCurrentProject.slides.length > 1;
                        const slideActive = selectedSlide?.id === slide.id;
                        return (
                        <div
                          key={slide.id}
                          draggable
                          onDragStart={() => setDraggingSlideId(slide.id)}
                          onDragEnd={() => setDraggingSlideId(null)}
                          onDragOver={(event) => {
                            event.preventDefault();
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            if (draggingSlideId && draggingSlideId !== slide.id && !reordering) {
                              void handleSlideReorder(draggingSlideId, slide.id);
                            }
                          }}
                          className={`group relative w-full rounded-3xl border p-2.5 text-left transition ${
                            slideActive
                              ? 'border-sky-200 bg-sky-50/90 shadow-lg shadow-sky-100/70'
                              : 'border-white/80 bg-white/90 hover:border-slate-200 hover:bg-white hover:shadow-md'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedSlideId(slide.id)}
                            className="w-full text-left"
                          >
                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                              {slide.image?.resultUrl ? (
                                <img src={resolveBrowserMediaUrl(slide.image.resultUrl)} alt={slide.title || `slide-${slide.sortOrder + 1}`} className="aspect-[16/9] w-full object-cover" />
                              ) : (
                                <div className="flex aspect-[16/9] items-center justify-center">
                                  <ImageIcon className="h-5 w-5 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="mt-2 flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="truncate pr-10 text-sm font-semibold text-slate-900">{slideDraft.title || `第 ${slide.sortOrder + 1} 页`}</div>
                                <div className="mt-1 flex items-center gap-2">
                                  <span className="text-[11px] text-slate-500">{formatSlideTypeLabel(slideDraft.slideType)}</span>
                                  <span className={`rounded-full border px-2 py-0.5 text-[10px] ${slideStatusClass(slide.generationStatus)}`}>
                                    {slideStatusLabel(slide.generationStatus)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col items-center gap-1 pt-0.5 text-slate-400">
                                <GripVertical className={`h-4 w-4 transition ${draggingSlideId === slide.id ? 'text-sky-500' : 'group-hover:text-slate-500'}`} />
                                <span className="text-[10px]">{slide.sortOrder + 1}</span>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => openDeleteSlideDialog(slide.id)}
                            disabled={!canDeleteSlide || deletingSlideId !== null}
                            className={`absolute right-3 top-3 z-10 rounded-xl border border-white/80 bg-white/95 p-2 text-slate-500 shadow-sm transition hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 sm:opacity-0 sm:group-hover:opacity-100 ${
                              slideActive ? 'sm:opacity-100' : ''
                            }`}
                            title={canDeleteSlide ? '删除本页' : '至少保留 1 页'}
                          >
                            {deletingSlideId === slide.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  </aside>

                  <div className="space-y-5 border-b border-slate-100 p-4 sm:p-6 xl:border-b-0 xl:border-r">
                    <div className="overflow-hidden rounded-[28px] border border-slate-100 bg-slate-50/80">
                      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:px-6 sm:py-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0">
                          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500 shadow-sm">
                            <Presentation className="h-3.5 w-3.5 text-sky-500" />
                            当前页面预览
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-xl font-semibold text-slate-950 sm:text-2xl">
                              {selectedSlideDraft?.title || selectedSlide?.title || `${resolvedCurrentProject.title} 预览`}
                            </h2>
                            {selectedSlide && (
                              <span className={`rounded-full border px-2.5 py-1 text-[11px] ${slideStatusClass(selectedSlide.generationStatus)}`}>
                                {slideStatusLabel(selectedSlide.generationStatus)}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-slate-500">
                            {selectedSlide
                              ? `第 ${selectedSlide.sortOrder + 1} 页 · ${formatSlideTypeLabel(selectedSlideType)}`
                              : `${resolvedCurrentProject.pageCount} 页 · ${stylePresetLabel}`}
                          </p>
                        </div>
                        {!resolvedCurrentProject.backgroundImageJobId && (
                          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                            请先生成并选定全局背景，再进行整套页面生成。
                          </div>
                        )}
                      </div>

                      <div className="p-4 sm:p-6">
                        <div className="rounded-[28px] border border-dashed border-slate-300 bg-white p-4">
                          <div className="group relative aspect-[16/9] overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 shadow-inner">
                            {selectedSlide?.image?.resultUrl ? (
                              <>
                                <img
                                  src={resolveBrowserMediaUrl(selectedSlide.image.resultUrl)}
                                  alt={selectedSlide.title || `slide-${selectedSlide.sortOrder + 1}`}
                                  className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 flex items-start justify-end bg-gradient-to-b from-slate-950/55 via-slate-950/10 to-transparent p-4 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setPreviewLightboxOpen(true)}
                                      className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/15 px-3 py-2 text-xs font-semibold text-white backdrop-blur-md transition hover:bg-white/25"
                                    >
                                      <Maximize2 className="h-3.5 w-3.5" />
                                      放大
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openInpaintDialog(selectedSlide.id)}
                                      disabled={actingSlideId === selectedSlide.id}
                                      className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/15 px-3 py-2 text-xs font-semibold text-white backdrop-blur-md transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                      <Wand2 className="h-3.5 w-3.5" />
                                      {formatCreditButtonLabel('局部重绘', creditsPreview.inpaint)}
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
                                <div className="space-y-3">
                                  <ImageIcon className="mx-auto h-8 w-8 text-slate-300" />
                                  <div>
                                    {selectedSlide
                                      ? selectedSlide.generationStatus === 'queued' || selectedSlide.generationStatus === 'processing'
                                        ? '当前页面正在生成，请稍候刷新结果'
                                        : '当前页面还没有生成结果'
                                      : '请先从左侧选择一个页面'}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {selectedSlide && selectedSlideDraft ? (
                      <>
                        <div className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-5 sm:p-6">
                          <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-900">
                            <History className="h-4 w-4 text-slate-500" />
                            历史版本
                          </div>
                          <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                            {selectedSlide.variants.length === 0 && (
                              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-3 py-8 text-center text-sm text-slate-500 lg:col-span-2 2xl:col-span-3">
                                当前页还没有历史版本
                              </div>
                            )}
                            {selectedSlide.variants.map((variant) => {
                              const active = selectedSlide.image?.id === variant.id;
                              return (
                                <div key={variant.id} className={`rounded-2xl border bg-white p-3 ${active ? 'border-sky-200 shadow-md shadow-sky-100/70' : 'border-slate-200'}`}>
                                  <div className="mb-3 overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
                                    {variant.resultUrl ? (
                                      <img src={resolveBrowserMediaUrl(variant.resultUrl)} alt="历史版本" className="aspect-[16/9] w-full object-cover" />
                                    ) : (
                                      <div className="flex aspect-[16/9] items-center justify-center text-sm text-slate-400">暂无预览</div>
                                    )}
                                  </div>
                                  <div className="mb-2 flex items-center justify-between gap-2">
                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${slideStatusClass(variant.status)}`}>
                                      {slideStatusLabel(variant.status)}
                                    </span>
                                    <span className="text-[10px] text-slate-400">
                                      {formatBeijingDateTime(variant.createdAt)}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => void handleRestoreVariant(selectedSlide.id, variant.id)}
                                    disabled={active || variant.status !== 'completed' || restoringVariantId === variant.id}
                                    className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/80 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {restoringVariantId === variant.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                                    {active ? '当前版本' : '恢复此版本'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                      </>
                    ) : (
                      <div className="rounded-[28px] border border-dashed border-slate-300 bg-slate-50/80 p-8 text-center">
                        <ImageIcon className="mx-auto mb-4 h-10 w-10 text-slate-300" />
                        <div className="text-lg font-semibold text-slate-900">请先从左侧选择一个页面</div>
                        <div className="mt-2 text-sm leading-6 text-slate-500">选中页面后，这里会显示当前页面预览和历史版本，图上蒙版提供放大和局部重绘操作。</div>
                      </div>
                    )}
                  </div>

                  <aside className="bg-slate-50/70 p-4 sm:p-5">
                    {selectedSlide && selectedSlideDraft ? (
                      <div className="space-y-5 xl:sticky xl:top-6">
                        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500 shadow-sm">
                          <Sparkles className="h-3.5 w-3.5 text-sky-500" />
                          提示词输入区域
                        </div>
                        <div className="space-y-3">
                          <label className="block space-y-1">
                            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">页面类型</span>
                            <select
                              value={selectedSlideDraft.slideType}
                              onChange={(e) => handleSlideDraftChange(selectedSlide.id, { slideType: e.target.value })}
                              className="block w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                            >
                              {SLIDE_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label} · {option.description}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="block space-y-1">
                            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">页面标题</span>
                            <input
                              value={selectedSlideDraft.title}
                              onChange={(e) => handleSlideDraftChange(selectedSlide.id, { title: e.target.value })}
                              className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                              placeholder="页面标题"
                            />
                          </label>
                          <label className="block space-y-1">
                            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">内容提示词</span>
                            <textarea
                              value={selectedSlideDraft.contentPrompt}
                              onChange={(e) => handleSlideDraftChange(selectedSlide.id, { contentPrompt: e.target.value })}
                              className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                              rows={8}
                              placeholder="基于统一背景图，描述这一页需要呈现的主体内容和视觉元素"
                            />
                          </label>
                          <div className="rounded-[24px] border border-slate-200 bg-white p-3">
                            <div className="mb-2 flex items-center justify-between gap-3">
                              <div>
                                <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">最终生图提示词预览</div>
                                <div className="mt-1 text-xs text-slate-400">此为提交给模型的最终提示词</div>
                              </div>
                              <button
                                type="button"
                                onClick={() => void handleCopySlidePrompt()}
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
                              >
                                <Copy className="h-3.5 w-3.5" />
                                复制
                              </button>
                            </div>
                            <textarea
                              value={selectedSlidePromptPreview}
                              readOnly
                              rows={8}
                              className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-xs leading-6 text-slate-700 outline-none"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleRegenerateSlide(selectedSlide)}
                            disabled={actingSlideId === selectedSlide.id}
                            className="mt-1 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500 px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-sky-200/70 transition hover:from-sky-600 hover:via-blue-600 hover:to-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {actingSlideId === selectedSlide.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : selectedSlide.image?.resultUrl ? (
                              <RefreshCw className="h-4 w-4" />
                            ) : (
                              <Sparkles className="h-4 w-4" />
                            )}
                            {formatCreditButtonLabel(selectedSlide.image?.resultUrl ? '重新生成' : '生成本页', creditsPreview.single)}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-[28px] border border-dashed border-slate-300 bg-slate-50/80 p-8 text-center">
                        <Sparkles className="mx-auto mb-4 h-8 w-8 text-slate-300" />
                        <div className="text-lg font-semibold text-slate-900">提示词区域</div>
                        <div className="mt-2 text-sm leading-6 text-slate-500">选中页面后，右侧会显示该页的页面标题和内容提示词，生成时会统一参考项目背景图。</div>
                      </div>
                    )}
                  </aside>
                </div>
              </section>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center p-6">
              <div className="max-w-md rounded-[36px] border border-white/70 bg-white/80 p-10 text-center shadow-2xl shadow-slate-200/80 backdrop-blur-xl">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-950 text-white">
                  <Presentation className="h-7 w-7" />
                </div>
                <p className="text-lg font-semibold text-slate-950">选择一个项目或创建新项目</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  图片式 PPT 工作台会基于统一背景来生成整套页面，并支持导出 `pptx`、`pdf` 和 `zip`。
                </p>
                <button
                  onClick={openCreateDialog}
                  className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  创建新项目
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
      {createDialogOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <button type="button" aria-label="关闭新建 PPT 弹窗" className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={closeCreateDialog} />
          <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">新建 PPT</div>
                <div className="mt-1 text-sm text-slate-500">请输入标题，并选择基础模型、重绘模型与默认画面质量。</div>
              </div>
              <button type="button" onClick={closeCreateDialog} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50" disabled={creating}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">PPT 标题</span>
                <input
                  autoFocus
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && form.title.trim() && !creating) {
                      void handleCreateProject();
                    }
                    if (e.key === 'Escape') {
                      closeCreateDialog();
                    }
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
                  placeholder="例如：AI 产品发布会"
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">基础模型</span>
                  <select
                    value={form.baseModelId}
                    onChange={(e) => setForm((prev) => ({ ...prev, baseModelId: e.target.value }))}
                    disabled={modelsLoading || baseModelOptions.length === 0}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {baseModelOptions.length === 0 ? (
                      <option value="">{modelsLoading ? '正在加载基础模型...' : '暂无可用基础模型'}</option>
                    ) : (
                      baseModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {formatModelLabel(model)}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="text-xs leading-5 text-slate-400">必须同时支持文生图和图生图，用于背景生成、整套生成和单页重生成。</div>
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">重绘模型</span>
                  <select
                    value={form.inpaintModelId}
                    onChange={(e) => setForm((prev) => ({ ...prev, inpaintModelId: e.target.value }))}
                    disabled={modelsLoading || inpaintModelOptions.length === 0}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {inpaintModelOptions.length === 0 ? (
                      <option value="">{modelsLoading ? '正在加载重绘模型...' : '暂无可用重绘模型'}</option>
                    ) : (
                      inpaintModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {formatModelLabel(model)}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="text-xs leading-5 text-slate-400">必须支持局部重绘，仅用于当前项目后续的局部重绘操作。</div>
                </label>
              </div>
              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">画面质量</span>
                <select
                  value={form.generationQuality}
                  onChange={(e) => setForm((prev) => ({ ...prev, generationQuality: e.target.value as 'low' | 'medium' | 'high' }))}
                  className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                >
                  {QUALITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} · {option.description}
                    </option>
                  ))}
                </select>
                <div className="text-xs leading-5 text-slate-400">项目默认质量会用于背景生成、整套生成、单页重生成和局部重绘。</div>
              </label>
              {(baseModelOptions.length === 0 || inpaintModelOptions.length === 0) && !modelsLoading && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-700">
                  {baseModelOptions.length === 0
                    ? '当前没有同时支持文生图和图生图的基础模型，请先去模型管理中启用。'
                    : '当前没有支持局部重绘的模型，请先去模型管理中启用。'}
                </div>
              )}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-500">
                默认按 8 页、16:9、商务风格创建。基础模型用于背景生成与页面生成，重绘模型用于局部重绘，画面质量会作为项目默认出图质量。
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeCreateDialog}
                  disabled={creating}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleCreateProject()}
                  disabled={creating || modelsLoading || !form.title.trim() || !form.baseModelId || !form.inpaintModelId}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  创建项目
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {editingProject && (
        <div className="fixed inset-0 z-[82] flex items-center justify-center px-4">
          <button type="button" aria-label="关闭编辑项目弹窗" className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={closeEditProjectDialog} />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">编辑项目</div>
                <div className="mt-1 text-sm text-slate-500">可修改标题、基础模型、重绘模型和默认画面质量。</div>
              </div>
              <button type="button" onClick={closeEditProjectDialog} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50" disabled={savingProject}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">项目标题</span>
                <input
                  autoFocus
                  value={editForm.title}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && editForm.title.trim() && !savingProject) {
                      void handleUpdateProject();
                    }
                    if (e.key === 'Escape') {
                      closeEditProjectDialog();
                    }
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
                  placeholder="请输入项目标题"
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">基础模型</span>
                  <select
                    value={editForm.baseModelId}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, baseModelId: e.target.value }))}
                    disabled={modelsLoading || baseModelOptions.length === 0}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {baseModelOptions.length === 0 ? (
                      <option value="">{modelsLoading ? '正在加载基础模型...' : '暂无可用基础模型'}</option>
                    ) : (
                      baseModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {formatModelLabel(model)}
                        </option>
                      ))
                    )}
                  </select>
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">重绘模型</span>
                  <select
                    value={editForm.inpaintModelId}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, inpaintModelId: e.target.value }))}
                    disabled={modelsLoading || inpaintModelOptions.length === 0}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {inpaintModelOptions.length === 0 ? (
                      <option value="">{modelsLoading ? '正在加载重绘模型...' : '暂无可用重绘模型'}</option>
                    ) : (
                      inpaintModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {formatModelLabel(model)}
                        </option>
                      ))
                    )}
                  </select>
                </label>
              </div>
              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">画面质量</span>
                <select
                  value={editForm.generationQuality}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, generationQuality: e.target.value as 'low' | 'medium' | 'high' }))}
                  className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                >
                  {QUALITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} · {option.description}
                    </option>
                  ))}
                </select>
              </label>
              {(baseModelOptions.length === 0 || inpaintModelOptions.length === 0) && !modelsLoading && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-700">
                  {baseModelOptions.length === 0
                    ? '当前没有同时支持文生图和图生图的基础模型，请先去模型管理中启用。'
                    : '当前没有支持局部重绘的模型，请先去模型管理中启用。'}
                </div>
              )}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-500">
                当前项目：{editingProject.title} · 默认画面质量 {formatQualityLabel(editingProject.generationQuality || 'low')}
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeEditProjectDialog}
                  disabled={savingProject}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleUpdateProject()}
                  disabled={savingProject || modelsLoading || !editForm.title.trim() || !editForm.baseModelId || !editForm.inpaintModelId}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingProject ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                  保存修改
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deletingProject && (
        <div className="fixed inset-0 z-[83] flex items-center justify-center px-4">
          <button type="button" aria-label="关闭删除项目弹窗" className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={closeDeleteProjectDialog} />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">删除项目</div>
                <div className="mt-1 text-sm text-slate-500">该操作会删除项目及其关联页面记录，请谨慎确认。</div>
              </div>
              <button
                type="button"
                onClick={closeDeleteProjectDialog}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={deletingProjectId !== null}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
                你将删除项目《{deletingProject.title}》，删除后无法恢复。
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDeleteProjectDialog}
                  disabled={deletingProjectId !== null}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeleteProject()}
                  disabled={deletingProjectId !== null}
                  className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deletingProjectId !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  确认删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deletingSlide && (
        <div className="fixed inset-0 z-[84] flex items-center justify-center px-4">
          <button type="button" aria-label="关闭删除幻灯片弹窗" className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={closeDeleteSlideDialog} />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">删除幻灯片</div>
                <div className="mt-1 text-sm text-slate-500">该操作会删除当前页面并重排页码，请谨慎确认。</div>
              </div>
              <button
                type="button"
                onClick={closeDeleteSlideDialog}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={deletingSlideId !== null}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
                你将删除第 {deletingSlide.sortOrder + 1} 页《{getSlideDraft(deletingSlide).title || `第 ${deletingSlide.sortOrder + 1} 页`}》，删除后无法恢复。
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-700">
                当前项目至少保留 1 页；删除后会自动选中相邻页面。
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDeleteSlideDialog}
                  disabled={deletingSlideId !== null}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeleteSlide()}
                  disabled={deletingSlideId !== null}
                  className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deletingSlideId !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  确认删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {backgroundDialogOpen && resolvedCurrentProject && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center px-4 py-6">
          <button
            type="button"
            aria-label="关闭背景候选弹窗"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={closeBackgroundDialog}
          />
          <div className="relative z-10 flex max-h-[calc(100vh-48px)] w-full max-w-6xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">背景候选</div>
                <div className="mt-1 text-sm text-slate-500">在这里查看当前全局背景、生成候选并选择要应用到整套 PPT 的背景。</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={closeBackgroundDialog}
                  disabled={backgroundBusy}
                  className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-5 sm:p-6">
              <div className="grid gap-5 2xl:grid-cols-[320px_minmax(0,1fr)]">
                <div className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-5 sm:p-6">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-900">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    当前全局背景
                  </div>
                  <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-white">
                    {resolvedCurrentProject.backgroundImage?.resultUrl ? (
                      <img src={resolveBrowserMediaUrl(resolvedCurrentProject.backgroundImage.resultUrl)} alt="全局背景" className="h-56 w-full object-cover" />
                    ) : (
                      <div className="flex h-56 items-center justify-center text-sm text-slate-500">尚未选择背景</div>
                    )}
                  </div>
                  <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50/80 px-4 py-3 text-xs leading-6 text-emerald-700">
                    已选中的全局背景会保持锁定，后续重新生成候选也不会自动替换当前背景。
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-900">背景提示词</div>
                    <div className="text-xs text-slate-400">每次生成 1 张</div>
                  </div>
                  <textarea
                    value={backgroundPromptDraft}
                    onChange={(e) => setBackgroundPromptDraft(e.target.value)}
                    className="min-h-36 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    placeholder="编辑整套 PPT 的背景提示词"
                  />
                  <button
                    type="button"
                    onClick={() => void handleGenerateBackground()}
                    disabled={backgroundBusy}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {backgroundBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {formatCreditButtonLabel(
                      resolvedCurrentProject.backgroundCandidates.length > 0 ? '重新生成背景候选' : '生成背景候选',
                      creditsPreview.background
                    )}
                  </button>
                </div>

                <div className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-5 sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-900">背景候选</div>
                    <div className="text-xs text-slate-400">{resolvedCurrentProject.backgroundCandidates.length} 张</div>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {resolvedCurrentProject.backgroundCandidates.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-3 py-10 text-center text-sm text-slate-500">
                        {backgroundBusy ? '正在生成背景候选，请稍候...' : '先编辑背景提示词，再点击下方按钮生成 1 张候选'}
                      </div>
                    )}
                    {resolvedCurrentProject.backgroundCandidates.map((candidate) => {
                      const selected = resolvedCurrentProject.backgroundImageJobId === candidate.id;
                      return (
                        <div key={candidate.id} className={`overflow-hidden rounded-2xl border bg-white ${selected ? 'border-emerald-300' : 'border-slate-200'}`}>
                          <div className="aspect-video bg-slate-100">
                            {candidate.resultUrl ? (
                              <img src={resolveBrowserMediaUrl(candidate.resultUrl)} alt="背景候选" className="h-full w-full object-cover" />
                            ) : (
                              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                                {candidate.status === 'completed' ? '等待图片地址' : candidate.status === 'failed' ? '生成失败' : '生成中'}
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <span className={`rounded-full border px-2 py-0.5 text-[10px] ${slideStatusClass(candidate.status)}`}>
                                {slideStatusLabel(candidate.status)}
                              </span>
                              {selected && <span className="text-[10px] font-medium text-emerald-600">当前使用</span>}
                            </div>
                            <button
                              onClick={() => void handleSelectBackground(candidate.id)}
                              disabled={candidate.status !== 'completed' || selectingJobId === candidate.id}
                              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/80 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {selectingJobId === candidate.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                              {selected ? '已选为全局背景' : '设为全局背景'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {exportDialogOpen && resolvedCurrentProject && (
        <div className="fixed inset-0 z-[86] flex items-center justify-center px-4">
          <button type="button" aria-label="关闭导出弹窗" className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={closeExportDialog} />
          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">导出项目</div>
                <div className="mt-1 text-sm text-slate-500">选择要导出的格式，当前会导出项目「{resolvedCurrentProject.title || '未命名 PPT'}」。</div>
              </div>
              <button type="button" onClick={closeExportDialog} disabled={exportingFormat !== null} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 px-5 py-5 sm:px-6 sm:py-6">
              {EXPORT_OPTIONS.map((option) => {
                const isExporting = exportingFormat === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => void handleExport(option.value)}
                    disabled={exportingFormat !== null}
                    className="flex w-full items-center justify-between gap-4 rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4 text-left transition hover:border-sky-200 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{option.label}</div>
                      <div className="mt-1 text-xs leading-5 text-slate-500">{option.description}</div>
                    </div>
                    <div className="shrink-0 rounded-2xl bg-white p-2 text-slate-600 shadow-sm">
                      {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {batchGenerateDialogOpen && resolvedCurrentProject && (
        <div className="fixed inset-0 z-[87] flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="关闭整体生成确认弹窗"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={closeBatchGenerateDialog}
          />
          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">确认整体生成</div>
                <div className="mt-1 text-sm text-slate-500">确认后会按当前页面草稿和全局背景批量生成整套 PPT。</div>
              </div>
              <button
                type="button"
                onClick={closeBatchGenerateDialog}
                disabled={slideGenerating}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
              <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm leading-6 text-sky-800">
                预计消耗 {creditsPreview.batch ?? '-'} 积分，生成 {slideDrafts.length} 页。
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                当前项目：{resolvedCurrentProject.title || '未命名 PPT'}
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeBatchGenerateDialog}
                  disabled={slideGenerating}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleConfirmGenerateSlides()}
                  disabled={slideGenerating}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {slideGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  确认生成
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {aiOutlineDialogOpen && resolvedCurrentProject && (
        <div className="fixed inset-0 z-[87] flex items-center justify-center px-4 py-6">
          <button
            type="button"
            aria-label="关闭 AI 大纲弹窗"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={closeAiOutlineDialog}
          />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_100px_rgba(15,23,42,0.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <div className="text-lg font-semibold text-slate-950">AI 生成大纲</div>
                <div className="mt-1 text-sm text-slate-500">根据你提供的资料生成页面类型、标题和内容提示词，只做预览和填充，不会主动生图。</div>
              </div>
              <button
                type="button"
                onClick={closeAiOutlineDialog}
                disabled={aiOutlineStreaming}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
              <div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
                <div className="space-y-4">
                  <div className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-4">
                    <div className="mb-2 text-sm font-semibold text-slate-900">资料输入</div>
                    <div className="mb-3 text-xs leading-6 text-slate-500">
                      可输入主题背景、汇报目标、章节要点、参考文案、演讲对象等，AI 会严格按当前 {resolvedCurrentProject.slides.length} 页生成大纲。
                    </div>
                    <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs leading-6 text-amber-800">
                      当前会自动把资料输入和上一次生成结果保存在本浏览器的本地缓存中，刷新页面后仍可恢复；更换浏览器或设备不会同步，点击“清空”会删除当前项目的本地缓存。
                    </div>
                    <textarea
                      value={aiOutlineSourceText}
                      onChange={(e) => handleAiOutlineSourceTextChange(e.target.value)}
                      rows={12}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                      placeholder="例如：这是给客户的产品发布会 PPT，核心要讲市场机会、产品亮点、解决方案、案例和合作方式。受众为企业决策层，希望整体风格更商务、更有科技感。"
                    />
                    <div className="mt-4 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleClearAiOutline}
                        disabled={aiOutlineStreaming || (!aiOutlineSourceText && !aiOutlinePreviewText && aiOutlineDrafts.length === 0)}
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        清空
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleStartAiOutlineGeneration()}
                        disabled={aiOutlineStreaming}
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {aiOutlineStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                        {aiOutlineDrafts.length > 0 ? '重新生成' : '开始生成'}
                      </button>
                      {aiOutlineError && (
                        <div className="text-xs text-rose-500">{aiOutlineError}</div>
                      )}
                    </div>
                  </div>
                  <div className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-4">
                    <div className="mb-2 text-sm font-semibold text-slate-900">流式预览</div>
                    <textarea
                      ref={aiOutlinePreviewRef}
                      value={aiOutlinePreviewText}
                      readOnly
                      rows={16}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 font-mono text-xs leading-6 text-slate-700 outline-none"
                      placeholder={aiOutlineStreaming ? 'AI 正在流式生成大纲，请稍候...' : '点击“开始生成”后，这里会实时显示 AI 输出。'}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">大纲预览与修改</div>
                      <div className="mt-1 text-xs text-slate-500">生成完成后，可逐页修改页面类型、标题和内容提示词，再确认填充到当前幻灯片草稿。</div>
                    </div>
                    <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                      {aiOutlineDrafts.length || 0}/{resolvedCurrentProject.slides.length} 页
                    </div>
                  </div>
                  {aiOutlineDrafts.length > 0 ? (
                    <div className="space-y-3">
                      {aiOutlineDrafts.map((draft, index) => (
                        <div key={draft.id} className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <div className="text-sm font-semibold text-slate-900">第 {index + 1} 页</div>
                            <div className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500 shadow-sm">
                              {formatSlideTypeLabel(draft.slideType)}
                            </div>
                          </div>
                          <div className="space-y-3">
                            <label className="block space-y-1">
                              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">页面类型</span>
                              <select
                                value={draft.slideType}
                                onChange={(e) => handleAiOutlineDraftChange(draft.id, { slideType: e.target.value })}
                                className="block w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                              >
                                {SLIDE_TYPE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="block space-y-1">
                              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">页面标题</span>
                              <input
                                value={draft.title}
                                onChange={(e) => handleAiOutlineDraftChange(draft.id, { title: e.target.value })}
                                className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                                placeholder="页面标题"
                              />
                            </label>
                            <label className="block space-y-1">
                              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">内容提示词</span>
                              <textarea
                                value={draft.contentPrompt}
                                onChange={(e) => handleAiOutlineDraftChange(draft.id, { contentPrompt: e.target.value })}
                                rows={4}
                                className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                                placeholder="描述这一页应呈现的主体内容和视觉方向"
                              />
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50/80 px-6 py-16 text-center text-sm text-slate-500">
                      {aiOutlineStreaming ? 'AI 正在生成大纲，请稍候...' : '先输入资料并点击“开始生成”，生成结果会在这里形成可编辑预览。'}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={closeAiOutlineDialog}
                disabled={aiOutlineStreaming}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleApplyAiOutline}
                disabled={aiOutlineStreaming || !canApplyAiOutline}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                确认填充到幻灯片
              </button>
            </div>
          </div>
        </div>
      )}
      {previewLightboxOpen && selectedSlide?.image?.resultUrl && (
        <div className="fixed inset-0 z-[88] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <button
            type="button"
            aria-label="关闭放大预览"
            className="absolute inset-0"
            onClick={() => setPreviewLightboxOpen(false)}
          />
          <div className="relative z-10 w-full max-w-6xl overflow-hidden rounded-[28px] border border-white/10 bg-slate-950 shadow-[0_30px_100px_rgba(15,23,42,0.4)]">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 text-white sm:px-5">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{selectedSlideDraft?.title || selectedSlide.title || `第 ${selectedSlide.sortOrder + 1} 页`}</div>
                <div className="mt-1 text-xs text-slate-300">{formatSlideTypeLabel(selectedSlideType)}</div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewLightboxOpen(false)}
                className="rounded-xl p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="bg-slate-950 p-3 sm:p-5">
              <img
                src={resolveBrowserMediaUrl(selectedSlide.image.resultUrl)}
                alt={selectedSlide.title || `slide-${selectedSlide.sortOrder + 1}`}
                className="max-h-[80vh] w-full rounded-[20px] object-contain"
              />
            </div>
          </div>
        </div>
      )}
      {maskSlideId && resolvedCurrentProject && (() => {
        const slide = resolvedCurrentProject.slides.find((item) => item.id === maskSlideId);
        if (!slide?.image?.resultUrl) return null;
        return (
          <MaskDialog
            imageUrl={slide.image.resultUrl}
            showPromptInput
            submitLabel={formatCreditButtonLabel('提交重绘', creditsPreview.inpaint)}
            onClose={() => {
              if (actingSlideId === slide.id) return;
              setMaskSlideId(null);
            }}
            submitting={actingSlideId === slide.id}
            onSave={(payload) => {
              void handleSubmitInpaint(payload);
            }}
          />
        );
      })()}
    </AppShell>
  );
}
